import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  MessageSquare,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Copy,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { marketingAPI, WhatsAppTemplate } from '../../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../../services/logger.service';

// Pre-built WhatsApp template library
const TEMPLATE_LIBRARY: Partial<WhatsAppTemplate>[] = [
  {
    name: 'Welcome New Lead',
    content: `Hi {{firstName}}! 👋

Thank you for your interest in Something Property! 🏡

We're excited to help you find your perfect property. One of our expert agents will contact you within 24 hours.

In the meantime, feel free to browse our latest listings: [link]

Best regards,
Something Property Team`,
    category: 'welcome' as const,
    variables: ['firstName']
  },
  {
    name: 'Property Viewing Reminder',
    content: `Hi {{firstName}}! 🏡

This is a friendly reminder about your property viewing:

📍 Property: {{propertyName}}
📅 Date: {{viewingDate}}
⏰ Time: {{viewingTime}}
📌 Location: {{propertyLocation}}

Looking forward to showing you this amazing property!

{{agentName}}
Something Property`,
    category: 'follow-up' as const,
    variables: ['firstName', 'propertyName', 'viewingDate', 'viewingTime', 'propertyLocation', 'agentName']
  },
  {
    name: 'New Property Alert',
    content: `Hi {{firstName}}! 🎉

Great news! We found a property that matches your criteria:

🏡 {{propertyName}}
📍 {{propertyLocation}}
💰 R {{propertyPrice}}
🛏️ {{bedrooms}} Beds | 🚿 {{bathrooms}} Baths
📐 {{propertySize}} sqm

View details: [link]

Interested? Reply YES to schedule a viewing!

Something Property`,
    category: 'property-alert' as const,
    variables: ['firstName', 'propertyName', 'propertyLocation', 'propertyPrice', 'bedrooms', 'bathrooms', 'propertySize']
  },
  {
    name: 'Follow-up After Viewing',
    content: `Hi {{firstName}}! 😊

Thank you for viewing {{propertyName}} with us today!

What did you think? We'd love to hear your feedback.

Reply with:
✅ INTERESTED - if you'd like to proceed
📅 SCHEDULE - to view similar properties
💬 QUESTIONS - if you have any queries

We're here to help!

{{agentName}}
Something Property`,
    category: 'follow-up' as const,
    variables: ['firstName', 'propertyName', 'agentName']
  },
  {
    name: 'Offer Status Update',
    content: `Hi {{firstName}}! 📢

Important update on your offer for {{propertyName}}:

Status: {{offerStatus}}

{{statusMessage}}

Need to discuss? Give us a call or reply to this message.

{{agentName}}
Something Property`,
    category: 'promotional' as const,
    variables: ['firstName', 'propertyName', 'offerStatus', 'statusMessage', 'agentName']
  }
];

export function WhatsAppTemplates() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'custom' as WhatsAppTemplate['category'],
    variables: [] as string[]
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const { templates: data } = await marketingAPI.getWhatsAppTemplates();
      setTemplates(data);
    } catch (error) {
      logger.error('Failed to load WhatsApp templates', error as Error, undefined, 'WhatsAppTemplates');
      toast.error('Failed to load WhatsApp templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      // Extract variables from content
      const variables = extractVariables(formData.content);
      
      await marketingAPI.createWhatsAppTemplate({
        ...formData,
        variables
      });
      toast.success('WhatsApp template created successfully');
      setShowAddDialog(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      logger.error('Failed to create template', error as Error, undefined, 'WhatsAppTemplates');
      toast.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const variables = extractVariables(formData.content);
      
      await marketingAPI.updateWhatsAppTemplate(editingTemplate.id, {
        ...formData,
        variables
      });
      toast.success('WhatsApp template updated successfully');
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error) {
      logger.error('Failed to update template', error as Error, undefined, 'WhatsAppTemplates');
      toast.error('Failed to update template');
    }
  };

  const extractVariables = (content: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = content.matchAll(regex);
    const variables = new Set<string>();
    for (const match of matches) {
      variables.add(match[1]);
    }
    return Array.from(variables);
  };

  const openEditDialog = (template: WhatsAppTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category,
      variables: template.variables || []
    });
  };

  const openPreview = (template: WhatsAppTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      category: 'custom',
      variables: []
    });
  };

  const loadFromLibrary = (libraryTemplate: Partial<WhatsAppTemplate>) => {
    setFormData({
      name: libraryTemplate.name || '',
      content: libraryTemplate.content || '',
      category: libraryTemplate.category || 'custom',
      variables: libraryTemplate.variables || []
    });
    setShowTemplateLibrary(false);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryBadge = (category: WhatsAppTemplate['category']) => {
    const colors: Record<WhatsAppTemplate['category'], string> = {
      welcome: 'bg-green-500',
      'follow-up': 'bg-blue-500',
      'property-alert': 'bg-purple-500',
      newsletter: 'bg-orange-500',
      promotional: 'bg-pink-500',
      custom: 'bg-gray-500'
    };
    
    return (
      <Badge className={colors[category]}>
        {category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            WhatsApp Templates
          </h2>
          <p className="text-muted-foreground mt-1">
            Create and manage WhatsApp message templates for automated campaigns
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="marketing-whatsapp-create-button">
          <Plus className="w-4 h-4" />
          Create Template
        </Button>
      </div>

      {/* Search and Stats */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Card className="px-4 py-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{templates.length} Templates</span>
          </div>
        </Card>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            Loading templates...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            {searchQuery ? 'No templates found matching your search' : 'No templates found. Create your first WhatsApp template!'}
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Card className="hover:border-primary/50 transition-colors h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {getCategoryBadge(template.category)}
                  </div>
                  <CardDescription className="line-clamp-3 text-sm">
                    {template.content}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.variables.map((variable) => (
                      <Badge key={variable} variant="outline" className="text-xs">
                        {`{{${variable}}}`}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPreview(template)}
                      className="flex-1"
                      data-testid="marketing-whatsapp-preview-button"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      className="flex-1"
                      data-testid="marketing-whatsapp-edit-button"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(template.content);
                        toast.success('Template copied to clipboard');
                      }}
                      data-testid="marketing-whatsapp-copy-button"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Add/Edit Template Dialog */}
      <Dialog open={showAddDialog || editingTemplate !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingTemplate(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update template details' : 'Create a new WhatsApp message template'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {!editingTemplate && (
              <Button
                variant="outline"
                onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}
                className="gap-2"
                data-testid="marketing-whatsapp-library-toggle-button"
              >
                <Sparkles className="w-4 h-4" />
                {showTemplateLibrary ? 'Hide' : 'Browse'} Template Library
              </Button>
            )}

            {showTemplateLibrary && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Pre-built Templates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {TEMPLATE_LIBRARY.map((libTemplate, index) => (
                    <div
                      key={index}
                      className="p-3 border border-border/50 rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                      onClick={() => loadFromLibrary(libTemplate)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm">{libTemplate.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {libTemplate.category}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {libTemplate.content}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome New Lead"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value: WhatsAppTemplate['category']) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="property-alert">Property Alert</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                  <SelectItem value="promotional">Promotional</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Message Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter your WhatsApp message. Use {{variableName}} for dynamic content."
                rows={10}
                required
              />
              <p className="text-xs text-muted-foreground">
                Use {`{{variableName}}`} to insert dynamic content. Detected variables: {extractVariables(formData.content).join(', ') || 'None'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setEditingTemplate(null);
                resetForm();
              }}
              data-testid="marketing-whatsapp-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} data-testid="marketing-whatsapp-save-button">
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>WhatsApp Message Preview</DialogTitle>
            <DialogDescription>{previewTemplate?.name}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-[#dcf8c6] rounded-lg p-4 relative">
              <div className="absolute -left-2 top-0 w-0 h-0 border-t-[10px] border-t-transparent border-r-[10px] border-r-[#dcf8c6] border-b-[10px] border-b-transparent"></div>
              <p className="text-sm whitespace-pre-wrap text-gray-800">
                {previewTemplate?.content}
              </p>
              <p className="text-xs text-gray-500 mt-2 text-right">
                {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            {previewTemplate && previewTemplate.variables.length > 0 && (
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <p className="text-xs font-medium mb-2">Variables used:</p>
                <div className="flex flex-wrap gap-1">
                  {previewTemplate.variables.map((variable) => (
                    <Badge key={variable} variant="outline" className="text-xs">
                      {`{{${variable}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
